import React, { useState } from "react";
import { IconButton } from "@mui/material";
import { palette } from "../../styles/palette";
import KeyboardArrowLeft from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRight from "@mui/icons-material/KeyboardArrowRight";
import EmailStep from "./steps/EmailStep";
import PasswordStep from "./steps/PasswordStep";
import NameStep from "./steps/NameStep";
import PreferencesStep, { TeamInfo } from "./steps/PreferencesStep";
import "./MobileWizard.scss";
import { useNavigate } from "react-router-dom";
import LinearProgress, {
  linearProgressClasses,
} from "@mui/material/LinearProgress";
import DiscardChangesModal from "../components/DiscardChangesModal";
import {
  getRegisterErrorMessage,
  isDuplicateUserError,
  register,
} from "../../services/authService";

interface MobileWizardProps {
  isDesktop?: boolean;
}

export const MobileWizard: React.FC<MobileWizardProps> = ({ isDesktop }) => {
  const [activeStep, setActiveStep] = React.useState(0);
  const [userInfo, setUserInfo] = useState<UserInfo>(initInfo);
  const navigate = useNavigate();
  const [openModal, setOpenModal] = useState(false);
  const confirmAction = () => {
    navigate(-1);
    setOpenModal(false);
  };

  function createAccount() {
    register({
      username: userInfo.name,
      email: userInfo.email,
      password: userInfo.password,
      favouriteTeams: userInfo.preferences,
    })
      .then((response) => {
        // The API answers with HTTP 200 even when the username/email already
        // exists, so a successful request is not enough — check isSucceed.
        if (!response.isSucceed) {
          if (isDuplicateUserError(response)) {
            // Send the user back to the name step so they can pick a unique one.
            alert(
              "This name or email is already taken. Please choose a different name."
            );
            setActiveStep(2);
            return;
          }
          alert(getRegisterErrorMessage(response));
          return;
        }
        navigate("/login");
        alert(`User ${userInfo.name} created successfully!`);
      })
      .catch(() => alert("User couldn't be created - try once more."));
  }

  function handleBackClick() {
    if (activeStep > 0) {
      setActiveStep((prev) => prev - 1);
    } else {
      setOpenModal(true);
    }
  }
  return (
    <div
      style={{ width: isDesktop ? "100%" : undefined }}
      className={
        activeStep !== 3
          ? "mobileWizardPageLayout"
          : "mobileWizardPageLayoutPreferences"
      }
    >
      <div className="mobileWizard__navRow">
        <IconButton aria-label="Go back" onClick={handleBackClick} size="small">
          <KeyboardArrowLeft fontSize="large" />
        </IconButton>
        <div className="mobileWizard__stepLabel">
          Step {activeStep + 1} of 4 · {STEP_LABELS[activeStep]}
        </div>
        <KeyboardArrowRight fontSize="large" className="mobileWizard__hiddenArrow" />
      </div>
      <LinearProgress
        value={(activeStep + 1) * 25}
        variant="determinate"
        sx={{
          color: palette.accent,
          [`&.${linearProgressClasses.colorPrimary}`]: {
            backgroundColor: palette.bgSurface,
          },
          [`& .${linearProgressClasses.bar}`]: {
            borderRadius: 5,
            backgroundColor: palette.accent,
          },
        }}
      />
      {activeStep === 0 && (
        <div className="mobileWizard__stepContainer">
          <EmailStep
            setActiveStep={setActiveStep}
            setUserInfo={setUserInfo}
            userInfo={userInfo}
            isDesktop={isDesktop}
          />
          <DiscardChangesModal
            openModal={openModal}
            setOpenModal={setOpenModal}
            confirmAction={confirmAction}
          />
        </div>
      )}
      {activeStep === 1 && (
        <div className="mobileWizard__stepContainer">
          <PasswordStep
            setActiveStep={setActiveStep}
            setUserInfo={setUserInfo}
            userInfo={userInfo}
            isDesktop={isDesktop}
          />
        </div>
      )}
      {activeStep === 2 && (
        <div className="mobileWizard__stepContainer">
          <NameStep
            setActiveStep={setActiveStep}
            setUserInfo={setUserInfo}
            userInfo={userInfo}
            isDesktop={isDesktop}
          />
        </div>
      )}
      {activeStep === 3 && (
        <div className="mobileWizard__stepContainerLast">
          <PreferencesStep
            createAccount={createAccount}
            setUserInfo={setUserInfo}
            userInfo={userInfo}
            isDesktop={isDesktop}
          />
        </div>
      )}
    </div>
  );
};

// Step order shown in the progress label; index matches activeStep.
const STEP_LABELS = ["Email", "Password", "Name", "Teams"];

export interface UserInfo {
  email: string;
  name: string;
  password: string;
  // Local-only confirmation; never sent to the API (register picks fields).
  confirmPassword: string;
  preferences: TeamInfo[];
}

export const initInfo = {
  email: "",
  name: "",
  password: "",
  confirmPassword: "",
  preferences: [],
};
